import React from 'react';
import dayjs from 'shared/helpers/dayjs';

import * as Styled from 'dashboard/styles/Recharts.style';
import type { DateGrouping } from './UrgencySection';

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
  dataKey: string | number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  dateFormat: string;
  notesLabel: (count: number) => string;
  isVisible?: boolean;
  grouping?: DateGrouping;
}

function formatDateLabel(
  label: string | undefined,
  dateFormat: string,
  grouping: DateGrouping
) {
  if (grouping === 'monthly') {
    return dayjs(label).format('MMMM YYYY');
  }
  if (grouping === 'weekly') {
    return `${dayjs(label).format(dateFormat)} – ${dayjs(label)
      .endOf('isoWeek')
      .format(dateFormat)}`;
  }
  return dayjs(label).format(dateFormat);
}

export default function CustomTooltip({
  active,
  payload,
  label,
  dateFormat,
  notesLabel,
  grouping = 'daily',
}: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const total = payload.reduce(
    (sum, entry) => sum + (Number(entry.value) || 0),
    0
  );

  const reversed = [...payload].reverse();

  const dateLabel = formatDateLabel(label, dateFormat, grouping);

  return (
    <Styled.Tooltip>
      <Styled.TooltipSection>
        <Styled.TooltipTitle>{dateLabel}</Styled.TooltipTitle>
        <Styled.TooltipSubtitle>{notesLabel(total)}</Styled.TooltipSubtitle>
      </Styled.TooltipSection>
      <Styled.TooltipSection>
        {reversed.map((entry) => {
          const percentage =
            total === 0 ? 0 : Math.round((entry.value / total) * 100);
          return (
            <Styled.TooltipItem key={entry.dataKey}>
              <Styled.Dot $color={entry.color} />
              <Styled.TooltipItemLabel>
                {percentage}% {entry.name}
              </Styled.TooltipItemLabel>
            </Styled.TooltipItem>
          );
        })}
      </Styled.TooltipSection>
    </Styled.Tooltip>
  );
}
